import React, { memo } from 'react';
import type { OptionsList } from '@segmently/schema-models/v2';
import type { PreviewMode } from '../../../types/previewMode';
import { mapFrameLayoutStyles, mapOptionsListStyleToCSS } from '../../../utils/styleMapper';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { BulletListItem } from './BulletListItem';
import styles from './BulletList.module.css';

export interface BulletListProps {
  options: OptionsList | undefined;
  locale: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

export const BulletList = memo(function BulletList({
  options,
  locale,
  className,
  'data-testid': testId = 'bullet-list',
  editPath,
  previewMode,
}: BulletListProps): React.ReactElement | null {
  // Hook above the early return — the options block can materialize on a
  // mounted screen (hooks-order hardening pattern). Consumes the `options`
  // slot of the screen entrance — the Paywall renders its options as bullets,
  // so without this mount the slot silently never animated.
  const listAnim = useElementAnimation(editPath);

  if (!options || !options.items || options.items.length === 0) {
    return null;
  }

  const containerStyle = {
    ...mapFrameLayoutStyles(options.layout),
    ...mapOptionsListStyleToCSS(options.appearance),
    ...(options.appearance?.itemsSpacing !== undefined && { gap: options.appearance.itemsSpacing }),
    ...listAnim.style,
  };

  const containerClassName = mergeAnimationClassName(
    className ? `${styles.bulletList} ${className}` : styles.bulletList,
    listAnim,
  );

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      data-anim={listAnim['data-anim']}
      data-testid={testId}
      data-edit-path={editPath}
      role="list"
    >
      {options.items.map((item, index) => (
        <BulletListItem
          key={index}
          item={item}
          itemLayout={options.itemLayout}
          itemAppearance={options.appearance}
          locale={locale}
          data-testid={`${testId}-item-${index}`}
          editPath={editPath ? `${editPath}.items.${index}` : undefined}
          previewMode={previewMode}
        />
      ))}
    </div>
  );
});

BulletList.displayName = 'BulletList';
